/**
 * Shop — pure, deterministic stock generation + purchase rules.
 *
 * The shop is part of the seeded engine (not the renderer) so that:
 *  - the stock a shop node offers is reproducible from the run seed,
 *  - the balance bot can shop headlessly,
 *  - prices/rolls never touch Math.random and survive a save round-trip.
 *
 * A shop's stock is generated ONCE, the first time the shop node is entered,
 * and stored on the RunState (`run.shop`) so quitting + resuming mid-shop shows
 * the exact same wares with the exact same purchased state. Purchases mutate a
 * cloned RunState through the public api reducer (api.buyShopItem), keeping the
 * immutable-ish contract.
 */

import { Rng } from "./rng";
import { getCardDef, rewardCardIds } from "./content/cards";
import { getProgramDef, rewardProgramIds } from "./content/programs";
import { heatModifiers } from "./content/ascension";
import type {
  CardRarity,
  ProgramRarity,
  RunState,
  ShopCardItem,
  ShopProgramItem,
  ShopRemoveService,
  ShopState,
} from "./types";

export type {
  ShopCardItem,
  ShopProgramItem,
  ShopRemoveService,
  ShopState,
} from "./types";

/** Base prices by card rarity (pre-Heat). */
const CARD_PRICE: Record<CardRarity, [number, number]> = {
  starter: [20, 30],
  common: [40, 55],
  uncommon: [62, 82],
  rare: [95, 130],
  special: [20, 30],
};

/** Base prices by program rarity (pre-Heat). */
const PROGRAM_PRICE: Record<ProgramRarity, [number, number]> = {
  starter: [120, 150],
  common: [120, 150],
  uncommon: [150, 190],
  rare: [210, 270],
  boss: [260, 320],
};

export const SHOP_TUNING = {
  cardSlots: 5,
  programSlots: 2,
  removeBasePrice: 55,
  /** Each card-removal purchased raises the next removal's price by this. */
  removePriceStep: 20,
} as const;

/** Round a price to a tidy multiple of 5 (nicer UI, stable). */
function tidy(n: number): number {
  return Math.max(5, Math.round(n / 5) * 5);
}

/**
 * Generate a shop's stock deterministically from the run RNG. Pulls distinct
 * cards from the reward pool + distinct programs (skipping any the runner
 * already owns), prices them by rarity, and applies the Heat gold multiplier so
 * shops cost more in a stingy economy. Pure given the rng; advances it.
 */
export function generateShop(run: RunState, rng: Rng): ShopState {
  const heat = heatModifiers(run.heat);
  // Heat's goldMult makes rewards smaller; mirror it onto prices (inverse) so a
  // stingier economy also means pricier shops, but clamp so it never explodes.
  const priceMult = heat.goldMult > 0 ? 1 / heat.goldMult : 1;
  const scale = (n: number) => tidy(n * priceMult);

  // --- Cards: distinct picks from the reward pool. -------------------------
  const cardPool = rng.shuffle(rewardCardIds());
  const cards: ShopCardItem[] = [];
  for (const defId of cardPool) {
    if (cards.length >= SHOP_TUNING.cardSlots) break;
    const def = getCardDef(defId);
    const [lo, hi] = CARD_PRICE[def.rarity];
    cards.push({
      kind: "card",
      slot: `c${cards.length}`,
      defId,
      price: scale(rng.int(lo, hi)),
      purchased: false,
    });
  }

  // --- Programs: distinct picks not already owned. -------------------------
  const programPool = rng
    .shuffle(rewardProgramIds())
    .filter((id) => !run.programs.includes(id));
  const programs: ShopProgramItem[] = [];
  for (const defId of programPool) {
    if (programs.length >= SHOP_TUNING.programSlots) break;
    const def = getProgramDef(defId);
    const [lo, hi] = PROGRAM_PRICE[def.rarity];
    programs.push({
      kind: "program",
      slot: `p${programs.length}`,
      defId,
      price: scale(rng.int(lo, hi)),
      purchased: false,
    });
  }

  const remove: ShopRemoveService = {
    slot: "remove",
    price: scale(SHOP_TUNING.removeBasePrice),
    used: 0,
  };

  return { cards, programs, remove };
}

/** Current price of the removal service (climbs with each use). */
export function removalPrice(shop: ShopState): number {
  return tidy(
    shop.remove.price + shop.remove.used * SHOP_TUNING.removePriceStep,
  );
}
